import type { SeoParentRedirectState } from '~/utils/seoDeadPayloads'
import type { ComputedRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { computed, toValue, watch } from 'vue'
import { buildSeoParentPath, logInvalidSeoPath } from '~/utils/seoDeadPayloads'

/**
 * Redirige vers l'URL parente (un segment de moins) lorsque le contenu SEO est absent.
 * Appeler `redirectIfDead()` depuis le setup de la page : `if (nav) await nav`
 * @param {object} options Options de configuration.
 * @param {() => string[]} options.pathSegments Getter des segments slug (réactif à la route).
 * @param {string} options.sectionRoot Racine de section (ex. `ecoles`).
 * @param {() => SeoParentRedirectState} options.state Getter agrégé : chargement + décision « page morte ».
 * @param {string} options.source Libellé court pour les logs (ex. `ecoles/[...slug]`).
 * @returns {{ parentRedirectPath: ComputedRef<string | null>; redirectIfDead: () => ReturnType<typeof navigateTo> | undefined }} Chemins et redirect à renvoyer depuis le setup.
 */
export function useSeoParentRedirect(options: {
  pathSegments: () => string[]
  sectionRoot: string
  state: () => SeoParentRedirectState
  source: string
}): {
  parentRedirectPath: ComputedRef<string | null>
  redirectIfDead: () => ReturnType<typeof navigateTo> | undefined
} {
  const route = useRoute()
  const { locale, defaultLocale } = useI18n()

  const parentRedirectPath = computed((): string | null => {
    const { loading, isDead } = options.state()
    if (loading || !isDead) return null

    const segments = options.pathSegments()
    const loc = toValue(locale)
    const def = toValue(defaultLocale)
    const parent = buildSeoParentPath({
      slugSegments: segments,
      sectionRoot: options.sectionRoot,
      locale: loc,
      defaultLocale: def,
    })

    if (!parent || route.path === parent) return null
    return parent
  })

  const redirectIfDead = (): ReturnType<typeof navigateTo> | undefined => {
    const parent = parentRedirectPath.value
    if (!parent) return undefined

    logInvalidSeoPath({
      fromPath: route.path,
      toPath: parent,
      locale: toValue(locale),
      source: options.source,
    })

    return navigateTo(parent as RouteLocationRaw, { replace: true, redirectCode: 302 })
  }

  if (import.meta.client) {
    const router = useRouter()
    watch(parentRedirectPath, (parent) => {
      const { loading } = options.state()
      if (!parent || route.path === parent || loading) return
      void router.replace(parent)
    })
  }

  return { parentRedirectPath, redirectIfDead }
}
